// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useDebounce } from '@shared/ui-kit/hooks';
import { type AssetsSearchResp } from '@shared/data-access/interfaces/api/ai/assets';
import { assetsApi } from '@shared/data-access/api/ai/assets-api';
import React, { type FocusEventHandler, useRef, useState, useMemo } from 'react';
import { ListSuggestions } from './list-suggestion';
import { useTranslation } from 'react-i18next';

interface AutocompleteInputProps<T> {
  className?: string;
  error: boolean;
  name: string;
  noMargin?: boolean;
  onChangeValue: (arg: T) => void;
  placeholder?: string;
  value: string;
  width?: string;
  onSelectValue?: (arg: T, value?: AssetsSearchResp) => void;
  leftIcon?: JSX.Element;
  rightIcon?: JSX.Element;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  disabled?: boolean;
  delay?: number;
  onChangeDetails?: (value: any) => void;
  assetType?: string;
  excludedTickers?: string[];
  required?: boolean;
}

export const AutocompleteInput = ({
  error,
  name,
  onChangeValue,
  onSelectValue,
  value,
  className,
  leftIcon,
  rightIcon,
  placeholder,
  onBlur,
  disabled = false,
  delay = 300,
  onChangeDetails,
  assetType,
  excludedTickers = [],
  required = true,
}: AutocompleteInputProps<React.ChangeEvent<HTMLInputElement>>) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const [renderSuggestions, setRenderSuggestions] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(-1);

  const debounceValue = useDebounce(value, delay);

  const { data, isFetching: isLoading } = assetsApi.useSearchQuery(
    {
      ticker: debounceValue.trim().toUpperCase(),
      name: debounceValue.trim().toUpperCase(),
      assetType: assetType,
      key_search: debounceValue.trim().toUpperCase(),
      isin: debounceValue.trim().toUpperCase(),
    },
    {
      skip: debounceValue.length <= 1,
    },
  );

  const allTickers = useMemo(() => {
    const results = data ?? [];
    if (excludedTickers.length === 0) return results;
    return results.filter(ticker => !excludedTickers.includes(ticker.ticker));
  }, [data, excludedTickers]);

  const handleSelectValue = (value: string) => {
    const e = {
      target: {
        value,
        name,
      },
    };

    onChangeValue(e as never);

    onChangeDetails?.(allTickers?.find(({ ticker }) => ticker === value));

    if (onSelectValue)
      onSelectValue(
        e as never,
        allTickers?.find(({ ticker }) => ticker === value),
      );

    setRenderSuggestions(false);

    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!allTickers) return;

    if (e.key === 'ArrowUp') {
      if (suggestionIndex === -1) {
        return;
      }
      setSuggestionIndex(suggestionIndex - 1);
    } else if (e.key === 'ArrowDown') {
      if (suggestionIndex + 1 === allTickers.length) {
        return;
      }
      setSuggestionIndex(suggestionIndex + 1);
    } else if (e.key === 'Enter') {
      setSuggestionIndex(suggestionIndex + 1);

      const index = suggestionIndex + (suggestionIndex > -1 ? 0 : 1);

      const e = {
        target: {
          value: allTickers[index]?.ticker,
          name,
        },
      };

      onChangeValue(e as never);

      onChangeDetails?.(allTickers[suggestionIndex + 1]);

      if (onSelectValue) onSelectValue(e as never, allTickers[suggestionIndex + 1]);

      setSuggestionIndex(-1);
      setRenderSuggestions(false);

      inputRef.current?.blur();
    }
  };

  const handleOverList = () => {
    setSuggestionIndex(-1);
  };

  return (
    <div className="relative min-w-full">
      <input
        className={`input-common ${className} input-${
          error ? 'valid' : 'invalid'
        } ${leftIcon && 'pl-10'}`}
        value={value}
        onFocus={() => setRenderSuggestions(true)}
        onChange={onChangeValue}
        onBlur={e => {
          setSuggestionIndex(-1);
          setRenderSuggestions(false);
          onBlur?.(e);
        }}
        data-testid="ticker"
        onKeyDown={handleKeyDown}
        name={name}
        placeholder={placeholder || t('common.ticker', { ns: 'common' })}
        required={required}
        type="text"
        autoComplete="off"
        disabled={disabled}
        ref={inputRef}
      />
      {leftIcon && <div className="absolute left-4 top-3">{leftIcon}</div>}
      {rightIcon && <div className="absolute right-4 top-3">{rightIcon}</div>}

      <ListSuggestions
        allTickers={allTickers}
        handleSelectValue={handleSelectValue}
        isLoading={isLoading}
        renderSuggestions={renderSuggestions}
        suggestionIndex={suggestionIndex}
        handleOverList={handleOverList}
      />
    </div>
  );
};
